import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const HIDDEN_ROUTES = [
  '/',
  '/login', '/register', '/signup', '/verify',
  '/forgot', '/reset', '/set-new-password', '/kyc',
  '/admin', '/two-factor'
];

export default function DailyIncomeBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [income, setIncome] = useState(null);
  const [hasTokens, setHasTokens] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState({ totalRentalDistributed: 3200, totalInvested: 391191 });
  const [visible, setVisible] = useState(true);

  const path = location.pathname;
  const isHidden = (
    path === '/' ||
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/signup') ||
    path.startsWith('/verify') ||
    path.startsWith('/forgot') ||
    path.startsWith('/reset') ||
    path.startsWith('/set-new-password') ||
    path.startsWith('/kyc') ||
    path.startsWith('/admin') ||
    path.startsWith('/two-factor')
  );

  useEffect(() => {
    apiClient.get('/api/public/stats').then(r => {
      if (r.data?.success) setStats(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    apiClient.get('/api/holdings')
      .then(r => {
        const h = r.data?.holdings || r.data || [];
        setHasTokens(Array.isArray(h) && h.length > 0);
        setLoaded(true);
      }).catch(() => { setLoaded(true); });
    apiClient.get('/api/rental-income/accrued')
      .then(r => {
        if (r.data?.totalAccrued !== undefined) setIncome(r.data.totalAccrued);
      }).catch(() => {});
  }, [user]);

  if (isHidden || !visible) return null;

  const paidOut = `Rs.${Math.round(stats.totalRentalDistributed || 3200).toLocaleString('en-IN')}+`;
  const invested = `Rs.${((stats.totalInvested || 391191) / 100000).toFixed(1)}L+`;

  const handleCTA = () => {
    if (!user) { navigate('/register'); return; }
    if (!loaded) return; // wait for holdings to load
    if (hasTokens) navigate('/rental-income');
    else navigate('/properties');
  };

  const ctaLabel = user && hasTokens && income > 0
    ? `Your Income: Rs.${income.toFixed(2)} ->`
    : user && hasTokens
    ? 'Claim Daily Income ->'
    : user
    ? 'Start Earning Daily Income ->'
    : 'Start Earning Today ->';

  const ctaColor = user && hasTokens && income > 0 ? '#22c55e' : '#FF6A00';

  return (
    <div style={{ background: '#0a0a0a', borderBottom: '1px solid #1f1f1f', fontFamily: '-apple-system, sans-serif', position: 'relative', zIndex: 100 }}>
      <style>{`
        @keyframes ecPulse { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.5)} 50%{box-shadow:0 0 0 5px rgba(34,197,94,0)} }
        @media(max-width:640px){ .ec-stats-row{display:none!important} }
        .ec-cta-btn:hover { opacity: 0.88; }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', gap: 12, flexWrap: 'wrap' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 200 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0, animation: 'ecPulse 2s infinite' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
              India's only platform paying{' '}
              <span style={{ color: '#FF6A00' }}>daily rental income</span>
              {' '}on real estate
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 1 }}>
              From Rs.100 · No lock-in · Exit anytime
            </div>
          </div>
        </div>

        <div className="ec-stats-row" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {[{ val: paidOut, label: 'Paid out' }, { val: invested, label: 'Invested' }, { val: 'Daily', label: 'Income' }].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', borderLeft: i === 0 ? 'none' : '1px solid #1f1f1f', padding: '0 16px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.val}</div>
              <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button className="ec-cta-btn" onClick={handleCTA} style={{ background: ctaColor, color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'opacity 0.15s' }}>
            {ctaLabel}
          </button>
          <button onClick={() => setVisible(false)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 18, padding: '2px 6px', lineHeight: 1 }}>
            &times;
          </button>
        </div>
      </div>
    </div>
  );
}
